const MAX_SAMPLES_PER_ORDER = 2;
const MAX_SAMPLES_PER_VARIANT = 1;
const SIMPLY_SAMPLE_HANDLE = 'simply-sample';

// This guards theme-owned journeys; direct Cart API or checkout requests can bypass storefront code.

export const SAMPLE_LIMIT_MESSAGES = {
  cart: 'Your cart can contain a maximum of two SIMPLY SAMPLE fragrances, with only one of each fragrance.',
  order: 'You can order a maximum of two SIMPLY SAMPLE fragrances per order.',
  unavailable: 'We could not verify your sample limit. Please refresh and try again.',
  variant: 'You can order only one of each SIMPLY SAMPLE fragrance.',
};

function isSampleItem(item, { productHandle, productId }) {
  if (productId !== undefined && productId !== null && productId !== '') {
    return String(item.product_id) === String(productId);
  }

  return item.handle === productHandle;
}

export function getSampleCartState(
  items,
  { productHandle = SIMPLY_SAMPLE_HANDLE, productId } = {},
) {
  const variantQuantities = new Map();
  let totalQuantity = 0;

  for (const item of items) {
    if (!isSampleItem(item, { productHandle, productId })) continue;

    const quantity = Number(item.quantity) || 0;
    const variantId = String(item.variant_id);
    totalQuantity += quantity;
    variantQuantities.set(variantId, (variantQuantities.get(variantId) ?? 0) + quantity);
  }

  return { totalQuantity, variantQuantities };
}

export function getSampleAddError(items, { productId, variantId }) {
  const { totalQuantity, variantQuantities } = getSampleCartState(items, { productId });

  if ((variantQuantities.get(String(variantId)) ?? 0) >= MAX_SAMPLES_PER_VARIANT) {
    return SAMPLE_LIMIT_MESSAGES.variant;
  }
  if (totalQuantity >= MAX_SAMPLES_PER_ORDER) return SAMPLE_LIMIT_MESSAGES.order;

  return '';
}

export function getSampleCartError(items, options) {
  const { totalQuantity, variantQuantities } = getSampleCartState(items, options);
  const hasDuplicateVariant = [...variantQuantities.values()].some(
    (quantity) => quantity > MAX_SAMPLES_PER_VARIANT,
  );

  return hasDuplicateVariant || totalQuantity > MAX_SAMPLES_PER_ORDER
    ? SAMPLE_LIMIT_MESSAGES.cart
    : '';
}

async function fetchCart() {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

  return response.json();
}

function showFormError(form, message) {
  const errorContainer = form.querySelector('.error-message');
  if (!errorContainer) return;

  errorContainer.textContent = message;
  errorContainer.style.display = 'block';
  errorContainer.focus({ preventScroll: true });
}

function clearFormError(form) {
  const errorContainer = form.querySelector('.error-message');
  if (!errorContainer) return;

  errorContainer.textContent = '';
  errorContainer.style.display = 'none';
}

function showCheckoutError(message) {
  const errorContainer = document.querySelector('[data-sample-limit-error]');
  if (!errorContainer) return false;

  errorContainer.textContent = message;
  errorContainer.hidden = false;
  errorContainer.focus({ preventScroll: true });
  return true;
}

function getCheckoutLink(target) {
  const link = target.closest?.('a[href]');
  if (!link) return null;

  const pathname = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
  return pathname.endsWith('/checkout') ? link : null;
}

function restoreButton(button, text) {
  if (!button) return;

  button.disabled = false;
  button.textContent = text;
}

function initialiseSampleOrderLimits() {
  const bypassedSubmissions = new WeakSet();
  const validatingForms = new WeakSet();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (bypassedSubmissions.has(form)) {
        bypassedSubmissions.delete(form);
        return;
      }

      const picker = form.closest('sample-variant-picker');
      if (picker) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (validatingForms.has(form)) return;

        const button = event.submitter || form.querySelector('[type="submit"]');
        const buttonText = button?.textContent;
        const variantId = new FormData(form).get('id');
        let submissionResumed = false;
        validatingForms.add(form);
        clearFormError(form);
        if (button) {
          button.disabled = true;
          button.textContent = 'Checking your cart...';
        }

        fetchCart()
          .then((cart) => {
            const error = getSampleAddError(cart.items, {
              productId: picker.dataset.productId,
              variantId,
            });
            if (error) {
              showFormError(form, error);
              return;
            }

            bypassedSubmissions.add(form);
            restoreButton(button, buttonText);
            submissionResumed = true;
            if (button) {
              form.requestSubmit(button);
            } else {
              form.requestSubmit();
            }
          })
          .catch(() => showFormError(form, SAMPLE_LIMIT_MESSAGES.unavailable))
          .finally(() => {
            validatingForms.delete(form);
            if (!submissionResumed) restoreButton(button, buttonText);
          });
        return;
      }

      if (form.id !== 'cartform' || event.submitter?.name !== 'checkout') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (validatingForms.has(form)) return;
      validatingForms.add(form);

      fetchCart()
        .then((cart) => {
          const error = getSampleCartError(cart.items, { productHandle: SIMPLY_SAMPLE_HANDLE });
          if (error) {
            showCheckoutError(error);
            return;
          }

          bypassedSubmissions.add(form);
          form.requestSubmit(event.submitter);
        })
        .catch(() => showCheckoutError(SAMPLE_LIMIT_MESSAGES.unavailable))
        .finally(() => validatingForms.delete(form));
    },
    true,
  );

  document.addEventListener(
    'click',
    (event) => {
      const link = getCheckoutLink(event.target);
      if (!link) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      fetchCart()
        .then((cart) => {
          const error = getSampleCartError(cart.items, { productHandle: SIMPLY_SAMPLE_HANDLE });
          if (!error) {
            window.location.assign(link.href);
            return;
          }

          if (!showCheckoutError(error)) window.location.assign(window.theme.routes.cart_url);
        })
        .catch(() => {
          if (!showCheckoutError(SAMPLE_LIMIT_MESSAGES.unavailable)) {
            window.location.assign(window.theme.routes.cart_url);
          }
        });
    },
    true,
  );
}

if (typeof document !== 'undefined') initialiseSampleOrderLimits();
